import { createMemo, Match, Switch, type JSX } from "solid-js";

import { deckWords, DeckWordsProvider } from "./deck_words";

import { SEPARATOR_DOT } from "~/util/html/codepoint";
import { isType } from "~/util/solid";

import styles from "./deck.module.css";
import { CareSlide } from "./slides/care_slide";
import { CostSlide } from "./slides/cost_slide";
import { CoverSlide } from "./slides/cover_slide";
import { FamilySlide } from "./slides/family_slide";
import { IfItWaitsSlide } from "./slides/if_it_waits_slide";
import { ImagesSlide } from "./slides/images_slide";
import { LibraryPageSlide } from "./slides/library_page_slide";
import { OptionsSlide } from "./slides/options_slide";
import { PictureSlide } from "./slides/picture_slide";
import { QuestionsSlide } from "./slides/questions_slide";
import { TextSlide } from "./slides/text_slide";
import { TimelineSlide } from "./slides/timeline_slide";
import { VisitsSlide } from "./slides/visits_slide";
import { WhatWeFoundSlide } from "./slides/what_we_found_slide";
import { YourTeethSlide } from "./slides/your_teeth_slide";
import type { Deck, PictureSrc, Slide as SlideData, SlideType } from "./types";

// The cover, the pictures, the practice's own pages and the closing sit on the night palette; the rest on paper.
const DARK_SLIDES: ReadonlySet<SlideType> = new Set<SlideType>([
  "cover",
  "what_we_found",
  "picture",
  "library_page",
  "images",
  "questions",
]);

/** "Cover", "What we found", "X-ray", "Your teeth", "If it waits", the family title, "Your visits", "Your options",
 *  "Your timeline", "After treatment", "Cost", a staff slide's headline, "Questions". */
export function slideLabel(slide: SlideData): string {
  switch (slide.type) {
    case "cover":
      return "Cover";
    case "what_we_found":
      return "What we found";
    case "picture":
      return slide.headline;
    case "library_page":
      return slide.headline;
    case "your_teeth":
      return "Your teeth";
    case "if_it_waits":
      return "If it waits";
    case "family":
      return slide.title;
    case "visits":
      return slide.visits.length === 1 ? "Your visit" : "Your visits";
    case "options":
      return "Your options";
    case "timeline":
      return "Your timeline";
    case "care":
      return "After treatment";
    case "cost":
      return "Cost";
    case "text":
      return slide.headline;
    case "images":
      return slide.headline;
    case "questions":
      return "Questions";
  }
}

// A family's headline is the library's sentence, so its eyebrow is what names the treatment.
function eyebrow(slide: SlideData): string {
  return slide.type === "family"
    ? `${slide.eyebrow} ${SEPARATOR_DOT} ${slide.title}`
    : slide.eyebrow;
}

function twoDigits(value: number): string {
  return String(value).padStart(2, "0");
}

/** One slide: the frame (eyebrow, office, footer, progress bar) around the body its type draws. */
export function Slide(props: {
  slide: SlideData;
  deck: Deck;
  number: number;
  total: number;
  pictureSrc: PictureSrc;
  failedPictureText?: string;
}): JSX.Element {
  const words = createMemo(() => deckWords(props.deck));
  return (
    <DeckWordsProvider value={words}>
      <article
        class={styles["slide"]}
        data-type={props.slide.type}
        data-tone={DARK_SLIDES.has(props.slide.type) ? "dark" : "light"}
        role="group"
        aria-roledescription="slide"
        aria-label={`Slide ${props.number} of ${props.total}`}
      >
        <div class={styles["s-in"]}>
          <header class={styles["s-top"]}>
            <span class={styles["s-eye"]}>{eyebrow(props.slide)}</span>
            <span class={styles["s-mark"]}>{props.deck.officeName}</span>
          </header>
          <div class={styles["s-body"]}>
            <Switch>
              <Match when={isType(props.slide, "cover")}>
                {(slide) => <CoverSlide slide={slide()} deck={props.deck} />}
              </Match>
              <Match when={isType(props.slide, "what_we_found")}>
                {(slide) => (
                  <WhatWeFoundSlide
                    slide={slide()}
                    pictureSrc={props.pictureSrc}
                    failedPictureText={props.failedPictureText}
                  />
                )}
              </Match>
              <Match when={isType(props.slide, "picture")}>
                {(slide) => (
                  <PictureSlide
                    slide={slide()}
                    pictureSrc={props.pictureSrc}
                    failedPictureText={props.failedPictureText}
                  />
                )}
              </Match>
              <Match when={isType(props.slide, "library_page")}>
                {(slide) => <LibraryPageSlide slide={slide()} pictureSrc={props.pictureSrc} />}
              </Match>
              <Match when={isType(props.slide, "your_teeth")}>
                {(slide) => <YourTeethSlide slide={slide()} />}
              </Match>
              <Match when={isType(props.slide, "if_it_waits")}>
                {(slide) => <IfItWaitsSlide slide={slide()} pictureSrc={props.pictureSrc} />}
              </Match>
              <Match when={isType(props.slide, "family")}>
                {(slide) => <FamilySlide slide={slide()} pictureSrc={props.pictureSrc} />}
              </Match>
              <Match when={isType(props.slide, "visits")}>
                {(slide) => <VisitsSlide slide={slide()} />}
              </Match>
              <Match when={isType(props.slide, "options")}>
                {(slide) => <OptionsSlide slide={slide()} />}
              </Match>
              <Match when={isType(props.slide, "timeline")}>
                {(slide) => <TimelineSlide slide={slide()} />}
              </Match>
              <Match when={isType(props.slide, "care")}>
                {(slide) => <CareSlide slide={slide()} />}
              </Match>
              <Match when={isType(props.slide, "cost")}>
                {(slide) => <CostSlide slide={slide()} />}
              </Match>
              <Match when={isType(props.slide, "text")}>
                {(slide) => <TextSlide slide={slide()} />}
              </Match>
              <Match when={isType(props.slide, "images")}>
                {(slide) => (
                  <ImagesSlide
                    slide={slide()}
                    pictureSrc={props.pictureSrc}
                    failedPictureText={props.failedPictureText}
                  />
                )}
              </Match>
              <Match when={isType(props.slide, "questions")}>
                {(slide) => <QuestionsSlide slide={slide()} />}
              </Match>
            </Switch>
          </div>
          <footer class={styles["s-foot"]}>
            <span>{words().t("preparedFor", { name: props.deck.patientName })}</span>
            <span class={styles["s-no"]}>
              {`${twoDigits(props.number)} / ${twoDigits(props.total)}`}
            </span>
          </footer>
          <div
            class={styles["s-prog"]}
            style={{ width: `${(props.number / props.total) * 100}%` }}
            aria-hidden="true"
          />
        </div>
      </article>
    </DeckWordsProvider>
  );
}
